// matcherController.js - Admin CRM Semantic Ingredient Matcher Diagnostic Controller
import { BADGE } from "./ui/classes.js";

let activeDeps = null;
let lastResultsData = null;

const PRESET_STANDARD = [
  "2 (213 g) cans wild pink salmon, traditional style",
  "3 cloves garlic, minced",
  "1 medium yellow onion, finely chopped",
  "2 tbsp extra virgin olive oil",
  "1/2 cup heavy whipping cream",
  "1 lb boneless skinless chicken breasts",
  "250 g lean ground beef",
  "1 tsp kosher salt",
  "1 cup uncooked jasmine rice",
  "2 cups low-sodium chicken broth"
].join("\n");

const PRESET_VARIETIES = [
  "shredded cheese, Mexican blend",
  "curry paste, green",
  "bell pepper, red",
  "sweetened condensed milk",
  "diced tomatoes, fire roasted",
  "plain Greek yogurt",
  "boneless skinless chicken thighs, diced",
  "fresh cilantro, minced"
].join("\n");

const PRESET_EDGECASES = [
  "exotic dragonfruit puree",
  "colloquial stinking rose",
  "strawberries",
  "onions",
  "1/4 tsp cayenne pepper",
  "sun-dried tomatoes in oil, drained",
  "mystery Andean tubers"
].join("\n");

export async function loadMatcherTemplate() {
  const containerEl = document.getElementById("sectionMatcherContainer");
  if (!containerEl) return false;

  try {
    const res = await fetch("templates/matcher.html");
    if (!res.ok) throw new Error(`HTTP ${res.status} fetching matcher.html`);
    const html = await res.text();
    containerEl.innerHTML = html;
    return true;
  } catch (err) {
    console.error("❌ Failed to load matcher template:", err);
    return false;
  }
}

export function initMatcherController(deps) {
  activeDeps = deps;
  const { functions, httpsCallable, showToast } = deps;

  const textarea = document.getElementById("matcherInputText");
  if (textarea && !textarea.value) {
    textarea.value = PRESET_STANDARD;
  }

  // Presets
  const btnStd = document.getElementById("btnPresetStandard");
  if (btnStd) btnStd.addEventListener("click", () => { if (textarea) textarea.value = PRESET_STANDARD; });

  const btnVar = document.getElementById("btnPresetVarieties");
  if (btnVar) btnVar.addEventListener("click", () => { if (textarea) textarea.value = PRESET_VARIETIES; });

  const btnEdge = document.getElementById("btnPresetEdgeCases");
  if (btnEdge) btnEdge.addEventListener("click", () => { if (textarea) textarea.value = PRESET_EDGECASES; });

  const btnClear = document.getElementById("btnPresetClear");
  if (btnClear) btnClear.addEventListener("click", () => { if (textarea) textarea.value = ""; });

  // Toggle raw JSON
  const btnToggleJson = document.getElementById("btnToggleRawJson");
  const rawJsonContainer = document.getElementById("matcherRawJsonContainer");
  if (btnToggleJson && rawJsonContainer) {
    btnToggleJson.addEventListener("click", () => {
      const isHidden = rawJsonContainer.style.display === "none";
      rawJsonContainer.style.display = isHidden ? "block" : "none";
      btnToggleJson.textContent = isHidden ? "Hide raw JSON" : "Raw JSON";
    });
  }

  // Run Matcher
  const btnRun = document.getElementById("btnRunMatcher");
  const btnRunText = document.getElementById("btnRunMatcherText");

  if (btnRun) {
    btnRun.addEventListener("click", async () => {
      const rawText = textarea ? textarea.value.trim() : "";
      if (!rawText) {
        if (showToast) showToast("Enter at least one ingredient line.", "warning");
        return;
      }

      const lines = rawText.split("\n").map(l => l.trim()).filter(Boolean);
      if (lines.length === 0) {
        if (showToast) showToast("No ingredient lines found.", "warning");
        return;
      }

      btnRun.disabled = true;
      if (btnRunText) btnRunText.textContent = `Resolving ${lines.length} lines…`;

      const startTime = performance.now();
      try {
        const resolveFn = httpsCallable(functions, "resolveIngredients");
        const res = await resolveFn({ ingredients: lines });
        const duration = Math.round(performance.now() - startTime);

        lastResultsData = res.data;
        renderMatcherResults(res.data, duration);

        if (showToast) {
          showToast(`Resolved ${lines.length} lines in ${duration} ms.`, "success");
        }
      } catch (err) {
        console.error("❌ resolveIngredients error:", err);
        if (showToast) {
          showToast(`The matcher failed: ${err.message || err.code || "unknown error"}`, "danger");
        }
      } finally {
        btnRun.disabled = false;
        if (btnRunText) btnRunText.textContent = "Resolve ingredients";
      }
    });
  }
}

function renderMatcherResults(data, clientDurationMs) {
  const resultsCard = document.getElementById("matcherResultsCard");
  const tableBody = document.getElementById("matcherResultsTableBody");
  const summaryEl = document.getElementById("matcherPerformanceSummary");
  const rawJsonEl = document.getElementById("matcherRawJsonText");

  if (resultsCard) resultsCard.style.display = "block";
  if (rawJsonEl) rawJsonEl.textContent = JSON.stringify(data, null, 2);

  const results = data?.results || [];
  const metrics = data?.metrics || {};

  // Update top stat cards
  const statTotal = document.getElementById("statMatcherTotalTested");
  const statT1 = document.getElementById("statMatcherTier1Hits");
  const statT2 = document.getElementById("statMatcherTier2Hits");
  const statUnmapped = document.getElementById("statMatcherUnmapped");

  if (statTotal) statTotal.textContent = String(results.length);
  if (statT1) statT1.textContent = String(metrics.tier_1_hits || results.filter(r => r.match_tier === "tier_1").length);
  if (statT2) statT2.textContent = String(metrics.tier_2_hits || results.filter(r => r.match_tier === "tier_2").length);
  if (statUnmapped) statUnmapped.textContent = String(metrics.unmapped || results.filter(r => r.match_tier === "unmapped").length);

  if (summaryEl) {
    const serverDuration = metrics.duration_ms ? `${metrics.duration_ms} ms on the server` : "";
    summaryEl.textContent = `${results.length} lines in ${clientDurationMs} ms${serverDuration ? ` (${serverDuration})` : ""}.`;
  }

  if (!tableBody) return;

  // Whole class strings per tier and confidence band, so Tailwind finds them (#1210).
  const TIER = {
    tier_1: `<span class="${BADGE.green}">Tier 1, lexical</span>`,
    tier_2: `<span class="${BADGE.blue}">Tier 2, vector</span>`,
    unmapped: `<span class="${BADGE.yellow}">No match</span>`
  };
  const band = (pct) => (pct >= 90
    ? { bar: "bg-green-500", text: "text-green-700 dark:text-green-400" }
    : pct >= 70
      ? { bar: "bg-blue-500", text: "text-blue-700 dark:text-blue-400" }
      : { bar: "bg-yellow-500", text: "text-yellow-700 dark:text-yellow-500" });

  tableBody.innerHTML = results.map((item) => {
    const pct = Math.round((item.confidence || 0) * 100);
    const { bar, text } = band(pct);
    return `
      <tr>
        <td class="py-3 pr-3 pl-4 whitespace-nowrap sm:pl-6">${TIER[item.match_tier] || TIER.unmapped}</td>
        <td class="min-w-48 px-3 py-3 text-sm font-medium text-gray-900 dark:text-white">${escapeHtml(item.raw_text || "")}</td>
        <td class="px-3 py-3 font-mono text-xs text-gray-500 dark:text-gray-400">${escapeHtml(item.clean_text || "")}</td>
        <td class="px-3 py-3 font-mono text-xs font-semibold whitespace-nowrap text-paprika-700 dark:text-paprika-300">${escapeHtml(item.uid || "")}</td>
        <td class="px-3 py-3 text-sm">
          <div class="font-medium text-gray-900 dark:text-white">${escapeHtml(item.display_name || "")}</div>
          <div class="text-xs text-gray-500 dark:text-gray-400">${escapeHtml(item.category || "Pantry")}</div>
        </td>
        <td class="px-3 py-3 text-sm whitespace-nowrap text-gray-700 dark:text-gray-300">${escapeHtml(item.display_unit || "count")} <span class="text-gray-400 dark:text-gray-500">/ ${escapeHtml(item.canonical_unit || "count")}</span></td>
        <td class="py-3 pr-4 pl-3 sm:pr-6">
          <div class="flex items-center gap-2">
            <div class="h-1.5 w-16 overflow-hidden rounded-full bg-gray-100 dark:bg-white/10"><div class="h-full ${bar}" style="width: ${pct}%"></div></div>
            <span class="text-xs font-semibold tabular-nums ${text}">${pct}%</span>
          </div>
        </td>
      </tr>`;
  }).join("");
}

function escapeHtml(str) {
  if (!str) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
